import { useEffect, useRef, useState, type FormEvent } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { streamCoach } from './stream'
import { makeAttachment, MAX_ATTACHMENTS, type AttachmentDraft } from './attachments'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  /** Optional preview thumbnails to render in the user bubble. Not
   *  re-uploaded on re-render. */
  thumbnails?: string[]
}

const STARTER_PROMPTS = [
  'How am I doing this month?',
  'Where can I cut spending?',
  'Help me plan for vacation',
  'Review my debt payoff',
]

/**
 * Web Coach — feature parity with the iOS CoachView (#58 phase 1):
 *
 *   - SSE streaming via streamCoach() against /coach/chat
 *   - Bearer-auth + IAM check before send
 *   - Markdown rendering (GFM tables + strikethrough + task lists)
 *   - Image attachments via paperclip → file input (drag/drop is a
 *     small follow-up)
 *
 * Voice + PDF attachments deferred to follow-ups (phase 2 ticket).
 */
export function CoachScreen() {
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([])
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const scrollAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ block: 'end' })
  }, [messages, streaming])

  // Object-URL cleanup so we don't leak previews across renders.
  useEffect(() => {
    return () => {
      for (const att of attachments) URL.revokeObjectURL(att.previewUrl)
    }
  }, [attachments])

  async function send(promptOverride?: string) {
    const text =
      promptOverride ??
      (draft.trim().length === 0 && attachments.length > 0
        ? 'What do you see in this?'
        : draft.trim())
    if (!text) return
    setError(null)

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      thumbnails: attachments.map((a) => a.previewUrl),
    }
    const wireAttachments = attachments.map((a) => ({
      mediaType: a.mediaType,
      dataBase64: a.dataBase64,
      filename: a.file.name,
    }))
    setMessages((prev) => [...prev, userMessage])
    setDraft('')
    setAttachments([])
    setStreaming(true)

    const assistantId = crypto.randomUUID()
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }])

    try {
      let acc = ''
      for await (const chunk of streamCoach({ message: text, attachments: wireAttachments })) {
        acc += chunk
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: acc } : m)),
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Coach stream failed')
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: m.content + '\n\n_(stream interrupted)_' }
            : m,
        ),
      )
    } finally {
      setStreaming(false)
    }
  }

  async function ingestFiles(files: FileList | null) {
    if (!files) return
    setError(null)
    const drafts: AttachmentDraft[] = []
    for (const file of Array.from(files)) {
      if (attachments.length + drafts.length >= MAX_ATTACHMENTS) break
      try {
        drafts.push(await makeAttachment(file))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Attachment failed')
      }
    }
    setAttachments((prev) => [...prev, ...drafts])
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    void send()
  }

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] max-w-3xl flex-col gap-3">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">🦞 Coach</h1>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => setMessages([])}
            className="rounded-md border border-zinc-300 px-2 py-1 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Clear
          </button>
        )}
      </header>

      <section
        aria-label="Coach conversation"
        className="flex-1 space-y-4 overflow-auto rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
      >
        {messages.length === 0 ? (
          <WelcomePanel onPick={(p) => void send(p)} />
        ) : (
          messages.map((m) => <Bubble key={m.id} message={m} />)
        )}
        <div ref={scrollAnchorRef} />
      </section>

      {error && (
        <p role="alert" className="rounded-md bg-overspent-subtle p-2 text-sm text-overspent">
          {error}
        </p>
      )}

      {attachments.length > 0 && (
        <div
          aria-label="Pending attachments"
          className="flex gap-2 overflow-x-auto rounded-md border border-zinc-200 bg-white p-2 dark:border-zinc-800 dark:bg-zinc-900"
        >
          {attachments.map((att) => (
            <div key={att.id} className="relative h-16 w-16 shrink-0">
              <img
                src={att.previewUrl}
                alt={`Attachment ${att.file.name}`}
                className="h-16 w-16 rounded object-cover"
              />
              <button
                type="button"
                aria-label={`Remove ${att.file.name}`}
                onClick={() => setAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                className="absolute -right-1 -top-1 rounded-full bg-black/60 px-1 text-xs text-white"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={attachments.length >= MAX_ATTACHMENTS}
          aria-label="Attach image"
          className="rounded-md border border-zinc-300 px-2 py-2 text-lg hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
        >
          📎
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          multiple
          hidden
          onChange={(e) => {
            void ingestFiles(e.target.files)
            if (e.target) e.target.value = ''
          }}
        />
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
          placeholder="Ask about your finances…"
          aria-label="Message"
          rows={1}
          className="min-h-[44px] flex-1 resize-none rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
        />
        <button
          type="submit"
          disabled={streaming || (draft.trim().length === 0 && attachments.length === 0)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {streaming ? '…' : 'Send'}
        </button>
      </form>
    </div>
  )
}

function Bubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'
  return (
    <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100'
        }`}
      >
        {message.thumbnails && message.thumbnails.length > 0 && (
          <div className="mb-2 flex gap-2">
            {message.thumbnails.map((url, i) => (
              <img
                key={url}
                src={url}
                alt={`Attached image ${i + 1}`}
                className="h-20 w-20 rounded object-cover"
              />
            ))}
          </div>
        )}
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  )
}

function WelcomePanel({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div className="space-y-4 py-6 text-center">
      <p className="text-zinc-500">
        Ask anything about your spending, goals, or budget. I&apos;ll pull from your data and
        suggest concrete next steps.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {STARTER_PROMPTS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            className="rounded-full border border-zinc-300 bg-white px-3 py-1 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}
